import { useLidoSDK, useGasFeeUSD } from '.'
import { useDebounce } from '~/hooks'
import { WithdrawToken } from '../Withdrawal/types'
import {
  FALLBACK_WITHDRAWAL_REQUEST_STETH_PERMIT_GAS_LIMIT,
  FALLBACK_WITHDRAWAL_REQUEST_WSTETH_PERMIT_GAS_LIMIT,
  FALLBACK_WITHDRAWAL_REQUEST_STETH_APPROVED_GAS_LIMIT,
  FALLBACK_WITHDRAWAL_REQUEST_WSTETH_APPROVED_GAS_LIMIT,
  MAX_WITHDRAWAL_REQUESTS_COUNT,
  ESTIMATE_ACCOUNT,
} from '../constants'
import { getLidoWithdrawalRequestPermitGasLimit } from '~/apis/lido'
import useSWR from 'swr'
import { Address } from 'viem'
import { useAccount } from 'wagmi'

export const useWithdrawRequestGasFee = ({
  token,
  isApprovalFlow,
  requestCount,
}: {
  token: WithdrawToken
  isApprovalFlow: boolean
  requestCount?: number
}) => {
  const { address: userAccount } = useAccount()
  const account = (userAccount ?? ESTIMATE_ACCOUNT) as Address
  const { withdraw } = useLidoSDK()

  const fallback =
    token === 'stETH'
      ? isApprovalFlow
        ? FALLBACK_WITHDRAWAL_REQUEST_STETH_APPROVED_GAS_LIMIT
        : FALLBACK_WITHDRAWAL_REQUEST_STETH_PERMIT_GAS_LIMIT
      : isApprovalFlow
      ? FALLBACK_WITHDRAWAL_REQUEST_WSTETH_APPROVED_GAS_LIMIT
      : FALLBACK_WITHDRAWAL_REQUEST_WSTETH_PERMIT_GAS_LIMIT

  const cappedRequestCount = Math.min(
    requestCount || 1,
    MAX_WITHDRAWAL_REQUESTS_COUNT,
  )
  const debouncedRequestCount = useDebounce(cappedRequestCount, 2000)

  const {
    data: permitGasLimit,
    isValidating: isPermitGasLimitValidating,
  } = useSWR(
    !isApprovalFlow
      ? [
          'lido-withdrawal-request-permit-gas-limit',
          token,
          debouncedRequestCount,
        ]
      : null,
    async () => {
      const response = await getLidoWithdrawalRequestPermitGasLimit(
        token,
        debouncedRequestCount,
      )
      return response ? BigInt(response.gasLimit || '0') : undefined
    },
  )

  const {
    data: approvalFlowGasLimit,
    isValidating: isApprovalFlowGasLimitValidating,
  } = useSWR(
    isApprovalFlow
      ? [
          'lido-withdrawal-request-approved-gas-limit',
          token,
          debouncedRequestCount,
        ]
      : null,
    async () => {
      const contract = await withdraw.contract.getContractWithdrawalQueue()
      const requestsStub = Array.from<bigint>({
        length: debouncedRequestCount,
      }).fill(100n)

      // @ts-ignore
      return await contract.estimateGas.requestWithdrawals(
        [requestsStub, account],
        {
          account,
        },
      )
    },
  )

  const gasLimit =
    (isApprovalFlow ? approvalFlowGasLimit : permitGasLimit) ??
    fallback * BigInt(debouncedRequestCount)

  const { gasFee, gasFeeUSD } = useGasFeeUSD(gasLimit)

  const isValidating = isApprovalFlow
    ? isApprovalFlowGasLimitValidating
    : isPermitGasLimitValidating

  return { gasLimit, gasFee, gasFeeUSD, isValidating }
}
